/* eslint-disable */
import { MEMORY_CONFIG } from '@/constants/memoryConfig'

export class BreedingPoolManager {
  static REVENUE_SPLIT = {
    OWNER: 0.70,        // 70% to former owner
    MAINTENANCE: 0.20,   // 20% to ranch maintenance (sink)
    POOL_FEE: 0.10      // 10% breeding pool management
  }

  /**
   * Get available breeding partners from the retired pool
   * @param {Object} selectedChocobo - The chocobo looking for a partner
   * @param {Object} gameStore - Game store instance
   * @returns {Array} Array of available breeding partners
   */
  static getAvailablePartners(selectedChocobo, gameStore) {
    if (!gameStore.retirementHistory || !selectedChocobo) {
      return []
    }

    // Filter retired chocobos that are available for breeding
    const availablePartners = gameStore.retirementHistory
      .filter(record => {
        // Must be available for breeding
        if (!record.breedingAvailable || !record.breedingStats) return false
        
        // Must meet level 5 minimum requirement for breeding
        if (record.breedingStats.level < 5) return false
        
        // Must be opposite gender
        if (record.breedingStats.gender === selectedChocobo.gender) return false
        
        // Can't breed with self (if somehow retired chocobo matches)
        if (record.chocoboId === selectedChocobo.id) return false
        
        return true
      })
      .map(record => this.formatBreedingPartner(record, gameStore))
      .sort((a, b) => {
        // Sort by quality score (highest first)
        return b.qualityScore - a.qualityScore
      })
      .slice(0, 15) // Limit to top 15 partners

    return availablePartners
  }

  /**
   * Format a retirement record as a breeding partner
   * @param {Object} retirementRecord - The retirement record
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Formatted breeding partner
   */
  static formatBreedingPartner(retirementRecord, gameStore) {
    const stats = retirementRecord.breedingStats
    const qualityScore = this.calculateQualityScore(stats, retirementRecord.careerEarnings)
    
    // Determine if this was a racing champion or transit chocobo
    const hasRaceHistory = stats.totalRaces > 0
    const chocoboType = hasRaceHistory ? 'Champion' : 'Transit Chocobo'
    
    return {
      // Core identification
      id: retirementRecord.chocoboId,
      name: retirementRecord.chocoboName,
      isRetiredBreeder: true,
      
      // Stats for breeding logic
      ...stats,
      
      // Breeding-specific data
      breedingPrice: retirementRecord.breedingPrice,
      qualityScore: qualityScore,
      
      // Former owner info
      formerOwner: retirementRecord.formerOwner,
      retiredDay: retirementRecord.day,
      
      // Career performance
      careerEarnings: retirementRecord.careerEarnings,
      retirementReason: retirementRecord.reason,
      
      // Display helpers
      displayName: `${retirementRecord.chocoboName} (${retirementRecord.formerOwner.name}'s)`,
      chocoboType: chocoboType,
      hasRaceHistory: hasRaceHistory,
      breedingHistory: this.getBreedingHistory(retirementRecord.chocoboId, gameStore)
    }
  }

  /**
   * Calculate quality score for sorting partners
   * @param {Object} breedingStats - The breeding stats
   * @param {number} careerEarnings - Career earnings
   * @returns {number} Quality score
   */
  static calculateQualityScore(breedingStats, careerEarnings) {
    let score = 0
    
    // Grade values
    const gradeValues = {
      'S+': 20, 'S': 18, 'S-': 16, 'A+': 14, 'A': 12, 'A-': 10,
      'B+': 8, 'B': 6, 'B-': 4, 'C+': 3, 'C': 2, 'C-': 1,
      'D+': 0, 'D': -1, 'D-': -2, 'E': -3, 'F': -4
    }
    
    // Stat grades
    score += gradeValues[breedingStats.gradeSpeed] || 0
    score += gradeValues[breedingStats.gradeStamina] || 0
    
    // X-factors
    if (breedingStats.hasSpeedXFactor) score += 10
    if (breedingStats.hasStaminaXFactor) score += 10
    
    // Level bonus
    score += breedingStats.level / 2
    
    // Career performance
    score += breedingStats.winRate / 5  // Win rate bonus
    score += Math.min(10, careerEarnings / 5000)  // Earnings bonus (capped)
    
    // Color rarity
    const colorValues = {
      'yellow': 0, 'blue': 3, 'green': 3, 'pink': 4,
      'black': 5, 'white': 8, 'red': 8, 'gold': 15
    }
    score += colorValues[breedingStats.color] || 0
    
    return Math.round(score)
  }

  /**
   * Get breeding history for a chocobo (how many times used for breeding)
   * @param {string} chocoboId - The chocobo ID
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Breeding usage stats
   */
  static getBreedingHistory(chocoboId, gameStore) {
    // This would track breeding usage - for now return placeholder
    // Could be enhanced to track actual breeding transactions
    return {
      timesUsed: 0,
      lastUsed: null,
      totalOffspring: 0
    }
  }

  /**
   * Process a breeding transaction with a retired chocobo
   * @param {Object} retiredPartner - The retired breeding partner
   * @param {Object} selectedChocobo - The player's chocobo
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Transaction result
   */
  static processBreeding(retiredPartner, selectedChocobo, gameStore) {
    const breedingCost = retiredPartner.breedingPrice
    
    // Check if player can afford it
    if (gameStore.playerOwner.money < breedingCost) {
      return {
        success: false,
        error: 'Insufficient funds for breeding fee',
        cost: breedingCost
      }
    }

    // Deduct breeding fee from player
    gameStore.playerOwner.money -= breedingCost
    
    // Distribute revenue
    this.distributeBreedingRevenue(retiredPartner, breedingCost, gameStore)
    
    // Update breeding usage (for future tracking)
    this.updateBreedingUsage(retiredPartner, gameStore)
    
    // Create breeding transaction record
    const transaction = {
      type: 'breeding',
      day: gameStore.currentDay,
      playerChocobo: {
        id: selectedChocobo.id,
        name: selectedChocobo.name
      },
      retiredPartner: {
        id: retiredPartner.id,
        name: retiredPartner.name,
        formerOwner: retiredPartner.formerOwner.name
      },
      cost: breedingCost,
      timestamp: Date.now()
    }
    
    // Add to recent transactions
    if (!gameStore.breedingTransactions) {
      gameStore.breedingTransactions = []
    }
    gameStore.breedingTransactions.unshift(transaction)
    
    // Keep only last 20 breeding transactions
    if (gameStore.breedingTransactions.length > 20) {
      gameStore.breedingTransactions = gameStore.breedingTransactions.slice(0, 20)
    }

    return {
      success: true,
      transaction: transaction,
      partner: retiredPartner
    }
  }

  /**
   * Distribute breeding revenue to former owner
   * @param {Object} retiredPartner - The retired breeding partner
   * @param {number} totalFee - Total breeding fee paid
   * @param {Object} gameStore - Game store instance
   */
  static distributeBreedingRevenue(retiredPartner, totalFee, gameStore) {
    const ownerShare = Math.round(totalFee * this.REVENUE_SPLIT.OWNER)
    const formerOwnerId = retiredPartner.formerOwner.id
    
    // Find the former owner and pay them
    const formerOwner = gameStore.owners.find(owner => owner.id === formerOwnerId)
    if (formerOwner) {
      formerOwner.money += ownerShare
      
      // Record breeding income for the owner
      if (!formerOwner.breedingIncome) {
        formerOwner.breedingIncome = 0
      }
      formerOwner.breedingIncome += ownerShare
      
      // Add notification if it's the player
      if (formerOwner.isPlayer) {
        this.addBreedingIncomeNotification(gameStore, {
          chocoboName: retiredPartner.name,
          amount: ownerShare,
          day: gameStore.currentDay
        })
      }
    }
    
    // The remaining money (maintenance + pool fee) is effectively removed from economy
    // This helps prevent inflation while rewarding former owners
  }

  /**
   * Add breeding income notification for player
   * @param {Object} gameStore - Game store instance  
   * @param {Object} incomeData - Income notification data
   */
  static addBreedingIncomeNotification(gameStore, incomeData) {
    if (!gameStore.breedingIncomeNotifications) {
      gameStore.breedingIncomeNotifications = []
    }
    
    gameStore.breedingIncomeNotifications.unshift({
      ...incomeData,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      read: false
    })
    
    // Keep only last 10 notifications
    if (gameStore.breedingIncomeNotifications.length > 10) {
      gameStore.breedingIncomeNotifications = gameStore.breedingIncomeNotifications.slice(0, 10)
    }
  }

  /**
   * Update breeding usage statistics
   * @param {Object} retiredPartner - The retired breeding partner
   * @param {Object} gameStore - Game store instance
   */
  static updateBreedingUsage(retiredPartner, gameStore) {
    // Find the retirement record and update usage
    const retirementRecord = gameStore.retirementHistory.find(
      record => record.chocoboId === retiredPartner.id
    )
    
    if (retirementRecord) {
      if (!retirementRecord.breedingUsage) {
        retirementRecord.breedingUsage = {
          timesUsed: 0,
          lastUsed: null,
          totalEarned: 0
        }
      }
      
      retirementRecord.breedingUsage.timesUsed++
      retirementRecord.breedingUsage.lastUsed = gameStore.currentDay
      retirementRecord.breedingUsage.totalEarned += retiredPartner.breedingPrice
    }
  }

  /**
   * Get breeding pool statistics
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Pool statistics
   */
  static getPoolStatistics(gameStore) {
    if (!gameStore.retirementHistory) {
      return {
        totalRetired: 0,
        availableForBreeding: 0,
        totalBreedings: 0,
        totalRevenue: 0
      }
    }

    const availableForBreeding = gameStore.retirementHistory.filter(
      record => record.breedingAvailable
    ).length

    const totalBreedings = gameStore.breedingTransactions?.length || 0
    const totalRevenue = gameStore.breedingTransactions?.reduce(
      (sum, transaction) => sum + transaction.cost, 0
    ) || 0

    return {
      totalRetired: gameStore.retirementHistory.length,
      availableForBreeding: availableForBreeding,
      totalBreedings: totalBreedings,
      totalRevenue: totalRevenue
    }
  }

  /**
   * Clean up old breeding data for memory management
   * @param {Object} gameStore - Game store instance
   */
  static cleanupBreedingData(gameStore) {
    // Remove old breeding transactions
    if (gameStore.breedingTransactions && gameStore.breedingTransactions.length > 50) {
      gameStore.breedingTransactions = gameStore.breedingTransactions.slice(0, 50)
    }
    
    // Remove old breeding income notifications
    if (gameStore.breedingIncomeNotifications && gameStore.breedingIncomeNotifications.length > 20) {
      gameStore.breedingIncomeNotifications = gameStore.breedingIncomeNotifications.slice(0, 20)
    }
    
    // Remove breeding usage data for very old retirements (older than 2 years)
    const cutoffDay = gameStore.currentDay - (2 * 12 * 32) // 2 game years
    gameStore.retirementHistory?.forEach(record => {
      if (record.day < cutoffDay && record.breedingUsage) {
        // Keep just basic stats, remove detailed usage
        record.breedingUsage = {
          timesUsed: record.breedingUsage.timesUsed,
          totalEarned: record.breedingUsage.totalEarned
        }
      }
    })
  }
}